import React, { useEffect, useRef, useState } from 'react'
import ContainerPage from "../../components/ContainerPage/ContainerPage"
import PageTitle from "../../components/PageTitle/PageTitle"
import TableFooter from "../../components/Pagination/TableFooter"
import { useDispatch, useSelector } from "react-redux"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { REQUEST_ERRORS_LISTS } from "../../constants/requestErrorFlags"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import DebounceInput from "../../components/DebouncedSearch/DebouncedSearch"
import PlusIcon from "../../assets/images/icons/PlusIcon.svg"
import Button from "../../components/Button/Button"
import { useNavigate } from "react-router-dom"
import { fetchAffiliates } from "../../features/affiliates/affiliates"
import AffiliatesTable from "./AffiliatesTable"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { getListCache, setListCache, clearListCache, buildCacheKey } from "../../utils/listCache"
import useURLState from "../../hooks/useURLState"
import Filters from "../../components/Filters/Filters"
import { initQueryData } from "../../components/Filters/helper"
import AppliedFilters from "../../components/AppliedFilters/AppliedFilters"
import Dropdown from "../../components/Dropdown/Dropdown"
import { AFFILIATE_STATUS_OPTIONS } from "./affiliateStatus"
import { TABLE_CARD } from "../../components/Table/tableCardClass"

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  ...AFFILIATE_STATUS_OPTIONS,
]

const Affiliates = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { triggerBar } = useMyContextLoading()
  const listStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_LISTS))
  const listStrictRef = useRef(listStrict)
  listStrictRef.current = listStrict

  const { page, limit, search, filters, sortField, sortOrder, affiliateStatus, setPage, setMultiple } = useURLState({
    page: { type: 'number', default: 1 },
    limit: { type: 'number', default: 10 },
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] },
    sortField: { type: 'string', default: '' },
    sortOrder: { type: 'string', default: '' },
    affiliateStatus: { type: 'string', default: '' },
  })

  const [filterData, setFilterData] = useState({ filters: [], utils: [] })
  const [requestFilters, setRequestFilters] = useState(() =>
    filters.length > 0 ? filters : [initQueryData]
  )
  const [affiliates, setAffiliates] = useState([])
  const [pageCount, setPageCount] = useState('')
  const [totalCount, setTotalCount] = useState(null)
  const [handleDelete, setHandleDelete] = useState('')
  const [isUpdated, setIsUpdated] = useState(false)

  const fetchContacts = async () => {
    const cacheKey = buildCacheKey('affiliates', { page, limit, search, filters, sortField, sortOrder, affiliateStatus })
    const cached = getListCache(cacheKey)
    if (cached) {
      setAffiliates(cached.affiliates)
      setPageCount(cached.pageCount)
      setTotalCount(cached.totalCount)
    }
    triggerBar(true)
    try {
      const offset = (page - 1) * limit
      const currentFilters = filters.length > 0 ? filters : [initQueryData]
      const apiFilters = currentFilters[0]?.filterType?.length > 0 ? currentFilters : ""

      const response = await dispatch(fetchAffiliates({
        limit,
        offset,
        searchQuery: search,
        filters: apiFilters,
        sortField,
        sortOrder,
        affiliateStatus: affiliateStatus || undefined,
      }))
      if (listStrictRef.current && response?.error) {
        showSnackBar("error", typeof response.payload === 'string' ? response.payload : 'Failed to fetch affiliates')
        triggerBar(false)
        return
      }
      const fresh = {
        affiliates: response?.payload?.data?.affiliates || [],
        pageCount: response?.payload?.data?.meta?.pageCount,
        totalCount: response?.payload?.data?.meta?.totalAffiliates,
      }
      setAffiliates(fresh.affiliates)
      setPageCount(fresh.pageCount)
      setTotalCount(fresh.totalCount)
      setListCache(cacheKey, fresh)
    } catch (error) {
      showSnackBar("error", error)
      console.error('An unexpected error occurred:', error)
    }
    triggerBar(false)
  }

  const affiliatesCacheGuard = useRef(true)
  useEffect(() => {
    if (affiliatesCacheGuard.current) {
      affiliatesCacheGuard.current = false
      return
    }
    clearListCache('affiliates')
  }, [handleDelete, isUpdated])

  useEffect(() => {
    fetchContacts()
  }, [search, handleDelete, limit, page, isUpdated, sortField, sortOrder, affiliateStatus, JSON.stringify(filters)])

  useEffect(() => {
    setRequestFilters(filters.length > 0 ? filters : [initQueryData])
  }, [JSON.stringify(filters)])

  // Typing one or two characters used to do nothing at all, with no explanation.
  const handleSearch = (e) => {
    setMultiple({ search: e || '', page: 1 })
  }

  const handleSort = (field, order) => {
    setMultiple({ sortField: field, sortOrder: order, page: 1 })
  }

  const applyFilters = (newFilters) => {
    setRequestFilters(newFilters)
    setMultiple({
      filters: newFilters[0]?.filterType?.length > 0 ? newFilters : [],
      page: 1,
    })
  }

  return (
    <div className="">
      <ContainerPage>
        <div className="flex justify-between items-center">
          <PageTitle title="Affiliates List" />
          <div className="flex gap-4">
            <Button classes="flex h-9 items-center gap-2" onClick={() => navigate("/affiliates/add")}>
              <img src={PlusIcon} alt="" />
              <span>Add Affiliate</span>
            </Button>
          </div>
        </div>
        <div className={`${TABLE_CARD} mt-6`}>
          <div className="flex flex-row justify-between items-center px-5 py-4">
            <div className="flex flex-col">
              <div className="flex flex-row">
                <DebounceInput customClassName={'mr-5 b-1'} value={search} setSearchValue={handleSearch}
                  placeholder="Search..." />
                <Dropdown
                  medium
                  value={affiliateStatus}
                  setValue={(value) => setMultiple({ affiliateStatus: value, page: 1 })}
                  menuItems={STATUS_FILTER_OPTIONS}
                  className="w-[180px] mr-5"
                  placeholder="All Statuses"
                />
                <Filters
                  isFilterList={false}
                  fetchByFilters={applyFilters}
                  requestFilters={requestFilters}
                  flow="affiliates"
                  onFilterDataLoaded={setFilterData}
                />
              </div>
            </div>
          </div>
          <div className="px-5">
            <AppliedFilters
              requestFilters={requestFilters}
              filterDefs={filterData.filters} utilDefs={filterData.utils}
              onRemove={(index) => {
                const active = requestFilters.filter(f => f.fieldName || f.operator || f.value)
                const updated = active.filter((_, i) => i !== index)
                setRequestFilters(updated.length > 0 ? updated : [initQueryData])
                setMultiple({ filters: updated.length > 0 ? updated : [], page: 1 })
              }}
              onClearAll={() => {
                setRequestFilters([initQueryData])
                setMultiple({ filters: [], page: 1 })
              }}
            />
          </div>
          <AffiliatesTable
            data={affiliates}
            setIsUpdated={setIsUpdated}
            isUpdated={isUpdated}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
          <TableFooter page={page} limit={limit} total={totalCount} pageCount={pageCount}
            setPage={setPage}
            setLimit={(newLimit) => setMultiple({ limit: newLimit, page: 1 })} />
        </div>
      </ContainerPage>
    </div>
  )
}

export default Affiliates
